import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpand, faCompress } from "@fortawesome/free-solid-svg-icons";
import Magnifier from "../Magnifier/Magnifier";
import MeasurementTools from "../MeasurementTools/MeasurementTools";
import AnnotationTools from "../AnnotationTools/AnnotationTools";
import NavigationTools from "../NavigationTools/NavigationTools";
import styles from "./FocusTool.module.css";

const addTransparency = (color, opacity = 0.2) => {
  if (!color) return `rgba(0, 0, 0, ${opacity})`;
  
  if (color.startsWith("rgba(")) {
    const match = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (match) {
      const r = parseInt(match[1]);
      const g = parseInt(match[2]);
      const b = parseInt(match[3]);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
  }
  
  if (color.startsWith("rgb(")) {
    const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (match) {
      const r = parseInt(match[1]);
      const g = parseInt(match[2]);
      const b = parseInt(match[3]);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
  }
  
  if (color.startsWith("#")) {
    const hex = color.slice(1);
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    } else if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
  }
  
  return color;
};

const FocusTool = forwardRef(function FocusTool(
  {
    image,
    name,
    boxes = [],
    showMagnifier,
    lensSize,
    lensZoom,
    selectMode,
    cropRect,
    onCrop,
    onToggleBoxVisibility,
    activeMeasurementTool = "none",
    measurementColor = "#3b82f6",
    activeAnnotationTool = "none",
    annotationColor = "#3b82f6",
    annotationStrokeWidth = 2,
    zoom = 1,
    onZoomChange,
    panOffset = { x: 0, y: 0 },
    onPanChange,
    brightness = 0,
    contrast = 0,
    sharpness = 0,
    colorFilter = "none",
    showConfidence = true,
    showBorder = true,
    showFill = true,
    hoveredBoxId = null,
  },
  ref
) {
  const imgElRef = useRef(null);
  const imageWrapperRef = useRef(null);
  const measurementToolsRef = useRef(null);
  const annotationToolsRef = useRef(null);
  const [selectionRect, setSelectionRect] = useState(null);
  const [selecting, setSelecting] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useImperativeHandle(
    ref,
    () => ({
      clearMeasurements: () => {
        measurementToolsRef.current?.clearMeasurements();
      },
      clearAnnotations: () => {
        annotationToolsRef.current?.clearAnnotations();
      },
    }),
    []
  );

  const getImageDisplayInfo = useCallback(() => {
    if (!imgElRef.current) return null;
    const img = imgElRef.current;
    const rect = img.getBoundingClientRect();
    const naturalWidth = img.naturalWidth || img.width;
    const naturalHeight = img.naturalHeight || img.height;
    
    if (!naturalWidth || !naturalHeight) return null;
    
    const displayedWidth = rect.width;
    const displayedHeight = rect.height;
    
    const imageAspect = naturalWidth / naturalHeight;
    const displayAspect = displayedWidth / displayedHeight;
    
    let actualImageWidth, actualImageHeight, offsetX, offsetY;
    
    if (imageAspect > displayAspect) {
      actualImageHeight = displayedHeight;
      actualImageWidth = displayedHeight * imageAspect;
      offsetX = (displayedWidth - actualImageWidth) / 2;
      offsetY = 0;
    } else {
      actualImageWidth = displayedWidth;
      actualImageHeight = displayedWidth / imageAspect;
      offsetX = 0;
      offsetY = (displayedHeight - actualImageHeight) / 2;
    }
    
    return {
      rect,
      naturalWidth,
      naturalHeight,
      actualImageWidth,
      actualImageHeight,
      offsetX,
      offsetY,
    };
  }, []);

  const toPercentRect = useCallback(
    (start, current) => {
    const info = getImageDisplayInfo();
    if (!info) return null;
    
      const { rect, actualImageWidth, actualImageHeight, offsetX, offsetY } =
        info;
    
    const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
    
    const clientX1 = start.x;
    const clientY1 = start.y;
    const clientX2 = current.x;
    const clientY2 = current.y;
    
    const localX1 = clientX1 - rect.left - offsetX;
    const localY1 = clientY1 - rect.top - offsetY;
    const localX2 = clientX2 - rect.left - offsetX;
    const localY2 = clientY2 - rect.top - offsetY;
    
    const clampedX1 = clamp(localX1, 0, actualImageWidth);
    const clampedY1 = clamp(localY1, 0, actualImageHeight);
    const clampedX2 = clamp(localX2, 0, actualImageWidth);
    const clampedY2 = clamp(localY2, 0, actualImageHeight);
    
    const left = Math.min(clampedX1, clampedX2);
    const top = Math.min(clampedY1, clampedY2);
    const width = Math.abs(clampedX2 - clampedX1);
    const height = Math.abs(clampedY2 - clampedY1);
    
    if (width < 1 || height < 1) return null;
    
    return {
      x: (left / actualImageWidth) * 100,
      y: (top / actualImageHeight) * 100,
      w: (width / actualImageWidth) * 100,
      h: (height / actualImageHeight) * 100,
    };
    },
    [getImageDisplayInfo]
  );

  const handleSelectStart = (e) => {
    if (!selectMode || !imgElRef.current) return;
    setStartPoint({ x: e.clientX, y: e.clientY });
    setSelecting(true);
    setSelectionRect(null);
  };

  const handleSelectMove = (e) => {
    if (!selectMode || !selecting || !imgElRef.current || !startPoint) return;
    const current = { x: e.clientX, y: e.clientY };
    const rect = toPercentRect(startPoint, current);
    if (rect) {
      setSelectionRect(rect);
    }
  };

  const handleSelectEnd = (e) => {
    if (!selectMode || !selecting || !imgElRef.current || !startPoint) return;
    const current = { x: e.clientX, y: e.clientY };
    const finalRect = toPercentRect(startPoint, current);
    if (finalRect) {
      setSelectionRect(finalRect);
      onCrop?.(finalRect);
    }
    setSelecting(false);
    setStartPoint(null);
  };

  useEffect(() => {
    setSelectionRect(cropRect || null);
  }, [cropRect]);

  const focusStyle = useMemo(() => {
    if (!cropRect) return {};
    const info = getImageDisplayInfo();
    if (!info) return {};
    
    const {
      actualImageWidth,
      actualImageHeight,
      rect,
    } = info;
    
    const cropW = (cropRect.w / 100) * actualImageWidth;
    const cropH = (cropRect.h / 100) * actualImageHeight;
    
    const cropAspect = cropW / cropH;
    const containerAspect = rect.width / rect.height;
    
    let scale;
    if (cropAspect > containerAspect) {
      scale = rect.width / cropW;
    } else {
      scale = rect.height / cropH;
    }
    
    const cropCenterXPercent = cropRect.x + cropRect.w / 2;
    const cropCenterYPercent = cropRect.y + cropRect.h / 2;
    
    const translateX = (50 - cropCenterXPercent) * scale;
    const translateY = (50 - cropCenterYPercent) * scale;
    
    return {
      transformOrigin: "center center",
      transform: `translate(${translateX}%, ${translateY}%) scale(${scale})`,
    };
  }, [cropRect, getImageDisplayInfo]);

  const renderBoxes = useCallback(() => {
    const svgStyle = {};
    const isCurrentlyFullscreen = !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    );
    
    if (imgElRef?.current && !isCurrentlyFullscreen) {
      const imgRect = imgElRef.current.getBoundingClientRect();
      const wrapper = imgElRef.current.parentElement;
      if (wrapper) {
        const wrapperRect = wrapper.getBoundingClientRect();
        svgStyle.left = `${
          ((imgRect.left - wrapperRect.left) / wrapperRect.width) * 100
        }%`;
        svgStyle.top = `${
          ((imgRect.top - wrapperRect.top) / wrapperRect.height) * 100
        }%`;
        svgStyle.width = `${(imgRect.width / wrapperRect.width) * 100}%`;
        svgStyle.height = `${(imgRect.height / wrapperRect.height) * 100}%`;
      }
    }

    return (
      <div className={styles.boxLayer} ref={boxLayerRef}>
        {boxes.map((d) => {
            const isHovered = hoveredBoxId === d.id;
            if (d.polygon && Array.isArray(d.polygon) && d.polygon.length > 0) {
            const path =
              d.polygon
                .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
                .join(" ") + " Z";
              
              return (
                <svg
                  key={d.id}
                  className={`${styles.polygonOverlay} ${isHovered ? styles.pulse : ""}`}
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  style={{
                    position: "absolute",
                    ...svgStyle,
                    pointerEvents: "none",
                  }}
                >
                  <path
                    d={path}
                    fill={showFill ? addTransparency(d.color, 0.2) : "none"}
                    stroke={showBorder ? d.color : "none"}
                    strokeWidth="0.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {showConfidence && (
                    <text
                      x={d.polygon[0]?.x || d.box.x}
                      y={(d.polygon[0]?.y || d.box.y) - 1}
                      fill={d.color}
                      fontSize="2.2"
                      fontWeight="bold"
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      {d.label} {(d.conf * 100).toFixed(0)}%
                    </text>
                  )}
                </svg>
              );
            }
            
            return (
              <div
                key={d.id}
                className={`${styles.box} ${isHovered ? styles.pulse : ""}`}
                style={{
                  left: `${d.box.x}%`,
                  top: `${d.box.y}%`,
                width: `${d.box.w}%`,
                height: `${d.box.h}%`,
                  borderColor: showBorder ? d.color : "transparent",
                  boxShadow: showBorder ? `0 0 0 1px ${d.color}` : "none",
                  backgroundColor: showFill ? addTransparency(d.color, 0.2) : "transparent",
                }}
              >
                {showConfidence && (
                  <span
                    className={styles.boxLabel}
                    style={{ backgroundColor: d.color }}
                  >
                    {onToggleBoxVisibility && (
                      <button
                        type="button"
                        className={styles.badgeClose}
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleBoxVisibility(d.id);
                        }}
                        aria-label={`Hide ${d.label}`}
                      >
                        ×
                      </button>
                    )}
                    {d.label} · {(d.conf * 100).toFixed(0)}%
                  </span>
                )}
              </div>
            );
          })}
      </div>
    );
  }, [boxes, showConfidence, showBorder, showFill, onToggleBoxVisibility, hoveredBoxId]);

  const activeClip = useMemo(() => {
    if (!cropRect) return null;
    
    const info = getImageDisplayInfo();
    if (!info) {
      return {
        clipPath: `polygon(${cropRect.x}% ${cropRect.y}%, ${
          cropRect.x + cropRect.w
        }% ${cropRect.y}%, ${cropRect.x + cropRect.w}% ${
          cropRect.y + cropRect.h
        }%, ${cropRect.x}% ${cropRect.y + cropRect.h}%)`,
      };
    }
    
    const { actualImageWidth, actualImageHeight, offsetX, offsetY, rect } =
      info;
    
    const clipX =
      (((cropRect.x / 100) * actualImageWidth + offsetX) / rect.width) * 100;
    const clipY =
      (((cropRect.y / 100) * actualImageHeight + offsetY) / rect.height) * 100;
    const clipW = (((cropRect.w / 100) * actualImageWidth) / rect.width) * 100;
    const clipH =
      (((cropRect.h / 100) * actualImageHeight) / rect.height) * 100;
    
    return {
      clipPath: `polygon(${clipX}% ${clipY}%, ${clipX + clipW}% ${clipY}%, ${
        clipX + clipW
      }% ${clipY + clipH}%, ${clipX}% ${clipY + clipH}%)`,
      width: `${clipW}%`,
      height: `${clipH}%`,
      left: `${clipX}%`,
      top: `${clipY}%`,
    };
  }, [cropRect, getImageDisplayInfo]);
  const showSelectionBox = (selecting || selectionRect) && selectionRect;

  const toggleFullscreen = useCallback(() => {
    if (!imageWrapperRef.current) return;

    if (!isFullscreen) {
      if (imageWrapperRef.current.requestFullscreen) {
        imageWrapperRef.current.requestFullscreen();
      } else if (imageWrapperRef.current.webkitRequestFullscreen) {
        imageWrapperRef.current.webkitRequestFullscreen();
      } else if (imageWrapperRef.current.mozRequestFullScreen) {
        imageWrapperRef.current.mozRequestFullScreen();
      } else if (imageWrapperRef.current.msRequestFullscreen) {
        imageWrapperRef.current.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  }, [isFullscreen]);

  const boxLayerRef = useRef(null);

  useEffect(() => {
    const updateBoxLayerPosition = () => {
      if (!imgElRef.current || !boxLayerRef.current) return;
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      
      if (isCurrentlyFullscreen) {
        const updatePosition = () => {
          if (!imgElRef.current || !boxLayerRef.current) return;
          
          const info = getImageDisplayInfo();
          if (!info) return;
          
          const { actualImageWidth, actualImageHeight, offsetX, offsetY } = info;
          const wrapper = imageWrapperRef.current;
          const img = imgElRef.current;
          
          if (!wrapper) return;
          
          const wrapperRect = wrapper.getBoundingClientRect();
          const imgRect = img.getBoundingClientRect();
          
          const actualLeft = imgRect.left - wrapperRect.left + offsetX;
          const actualTop = imgRect.top - wrapperRect.top + offsetY;
          
          boxLayerRef.current.style.cssText = `
            position: absolute !important;
            width: ${actualImageWidth}px !important;
            height: ${actualImageHeight}px !important;
            left: ${actualLeft}px !important;
            top: ${actualTop}px !important;
            right: auto !important;
            bottom: auto !important;
            margin: 0 !important;
            padding: 0 !important;
            box-sizing: border-box !important;
            inset: auto !important;
          `;
        };
        
        requestAnimationFrame(() => {
          updatePosition();
          requestAnimationFrame(updatePosition);
        });
      } else if (boxLayerRef.current) {
        boxLayerRef.current.style.width = '';
        boxLayerRef.current.style.height = '';
        boxLayerRef.current.style.left = '';
        boxLayerRef.current.style.top = '';
        boxLayerRef.current.style.right = '';
        boxLayerRef.current.style.bottom = '';
        boxLayerRef.current.style.position = '';
        boxLayerRef.current.style.margin = '';
        boxLayerRef.current.style.padding = '';
        boxLayerRef.current.style.boxSizing = '';
      }
    };

    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
      
      if (isCurrentlyFullscreen) {
        setTimeout(() => {
          updateBoxLayerPosition();
          const interval = setInterval(() => {
            const stillFullscreen = !!(
              document.fullscreenElement ||
              document.webkitFullscreenElement ||
              document.mozFullScreenElement ||
              document.msFullscreenElement
            );
            if (!stillFullscreen) {
              clearInterval(interval);
              return;
            }
            updateBoxLayerPosition();
          }, 100);
          setTimeout(() => clearInterval(interval), 2000);
        }, 150);
        window.addEventListener('resize', updateBoxLayerPosition);
        if (imgElRef.current) {
          imgElRef.current.addEventListener('load', updateBoxLayerPosition);
        }
      } else {
        updateBoxLayerPosition();
        window.removeEventListener('resize', updateBoxLayerPosition);
        if (imgElRef.current) {
          imgElRef.current.removeEventListener('load', updateBoxLayerPosition);
        }
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
      window.removeEventListener('resize', updateBoxLayerPosition);
      if (imgElRef.current) {
        imgElRef.current.removeEventListener('load', updateBoxLayerPosition);
      }
    };
  }, [getImageDisplayInfo]);

  const enhancedStyle = useMemo(() => {
    const filters = [];
    if (brightness !== 0) filters.push(`brightness(${1 + brightness / 100})`);
    if (contrast !== 0) filters.push(`contrast(${1 + contrast / 100})`);
    if (sharpness !== 0) {
      const amount = sharpness / 100;
      filters.push(`contrast(${1 + amount * 0.5})`);
    }
    if (colorFilter === "grayscale") filters.push("grayscale(100%)");
    else if (colorFilter === "sepia") filters.push("sepia(100%)");
    else if (colorFilter === "invert") filters.push("invert(100%)");
    return { filter: filters.length > 0 ? filters.join(" ") : "none" };
  }, [brightness, contrast, sharpness, colorFilter]);

  const imageStyle = useMemo(() => {
    if (cropRect) {
      return {
        ...focusStyle,
        ...enhancedStyle,
      };
    }
    
    const transforms = [];
    
    if (panOffset.x !== 0 || panOffset.y !== 0) {
      transforms.push(`translate(${panOffset.x}px, ${panOffset.y}px)`);
    }
    
    if (zoom !== 1) {
      transforms.push(`scale(${zoom})`);
    }
    
    const baseStyle = {
      transform: transforms.length > 0 ? transforms.join(" ") : "none",
      transformOrigin: "center center",
      ...enhancedStyle,
    };
    
    if (activeClip) {
      baseStyle.clipPath = activeClip.clipPath;
    }
    
    return baseStyle;
  }, [activeClip, focusStyle, panOffset, zoom, enhancedStyle, cropRect]);

  return (
    <div className={styles.imageWrapper} ref={imageWrapperRef}>
      {showMagnifier ? (
        <Magnifier
          src={image}
          alt={name || "Uploaded intraoral"}
          zoom={lensZoom}
          size={lensSize}
          imageClassName={styles.fullImage}
          imageRef={imgElRef}
          style={imageStyle}
        >
          <div
            className={styles.boxLayer}
            style={{ ...activeClip, ...focusStyle }}
          >
            {renderBoxes()}
          </div>
        </Magnifier>
      ) : (
        <>
          <img
            src={image}
            alt={name || "Uploaded intraoral"}
            className={styles.fullImage}
            ref={imgElRef}
            style={imageStyle}
          />
          <div
            className={styles.boxLayer}
            style={{ ...activeClip, ...focusStyle }}
          >
            {renderBoxes()}
          </div>
        </>
      )}

      <MeasurementTools
        ref={measurementToolsRef}
        activeTool={activeMeasurementTool}
        imageRef={imgElRef}
        color={measurementColor}
      />

      <AnnotationTools
        ref={annotationToolsRef}
        activeTool={activeAnnotationTool}
        imageRef={imgElRef}
        color={annotationColor}
        strokeWidth={annotationStrokeWidth}
      />

      <NavigationTools
        imageRef={imgElRef}
        zoom={zoom}
        onZoomChange={onZoomChange}
        onPanChange={onPanChange}
        onFullscreen={toggleFullscreen}
      />

      <div
        className={styles.selectionLayer}
        style={{
          pointerEvents:
            selectMode &&
            activeMeasurementTool === "none" &&
            activeAnnotationTool === "none"
              ? "auto"
              : "none",
        }}
        onMouseDown={handleSelectStart}
        onMouseMove={handleSelectMove}
        onMouseUp={handleSelectEnd}
      >
        {showSelectionBox && !cropRect && (
          <div
            className={styles.selectionBox}
            style={{
              left: `${selectionRect.x}%`,
              top: `${selectionRect.y}%`,
              width: `${selectionRect.w}%`,
              height: `${selectionRect.h}%`,
            }}
          />
        )}
      </div>
    </div>
  );
});

FocusTool.displayName = "FocusTool";

export default FocusTool;
